import { isCanonicalShortTermConditionLayerEntryJson } from 'features/dataLayers/canonicalShortTermConditionLayers'
import { useGetRolodexEntryDispenserCacheInterfaces } from 'features/rolodex/dispenser/rolodexEntryDispenserCache'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import merge from 'lodash/merge'
import React, { useCallback } from 'react'
import {
  CacheableRolodexEntryJson,
  createGeckoAssetModel,
  DataFrameSettings,
  GeckoActionLog,
  GeckoAerospaceGVI,
  GeckoAerospaceGVICameras,
  GeckoAlert,
  GeckoAssetHealthAction,
  GeckoAssetHealthPlan,
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoCase,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentDefaults,
  GeckoComponentDeficiency,
  GeckoCopcPointCloud,
  GeckoDerivedSpatialDataset,
  GeckoDocumentTimelineEntry,
  GeckoEntry,
  GeckoGeographicPin,
  GeckoHomepageConfig,
  GeckoInspection,
  GeckoInspectionForm,
  GeckoJob,
  GeckoKestrelDefectDetectionRun,
  GeckoKestrelDetectionTrainingData,
  GeckoKestrelModel,
  GeckoKestrelModelCameras,
  GeckoKestrelModelUnit,
  GeckoKestrelModelWhiteboard,
  GeckoLegacyBoilerAssetModel,
  GeckoLifecycleRecommendation,
  GeckoMachinedForgingAssetModel,
  GeckoMarkupLayer,
  GeckoMatterportSpace,
  GeckoMillPlan,
  GeckoNavbarConfig,
  GeckoNavyProductionDashboardConfig,
  GeckoObservationTicket,
  GeckoOrganization,
  GeckoPhotogrammetryModel,
  GeckoPipelineAssetModel,
  GeckoPointCloudScene,
  GeckoPotreePointCloud,
  GeckoProcessFlowDiagram,
  GeckoReport,
  GeckoReportingMetadata,
  GeckoRiskAssessmentConfig,
  GeckoRiskGate0Form,
  GeckoRiskScenario,
  GeckoSite,
  GeckoSiteAssetHierarchy,
  GeckoSiteConfig,
  GeckoSiteModel,
  GeckoSiteObservation,
  GeckoSource,
  GeckoSystem,
  GeckoThirdPartyInspectionDetails,
  GeckoUnit,
  GeckoUserComment,
  GeckoUserList,
  GeckoValueStory,
  GeckoVerticalTankAssetModel,
  GeckoView,
  GeckoVisualInspection,
  GeckoVisualInspectionField,
  GeckoVisualInspectionFieldResponse,
  GeckoVisualInspectionForm,
  GeckoVisualInspectionFormResponse,
  GeckoVisualInspectionSection,
  GeckoVisualInspectionSectionResponse,
  GeckoWalkdown,
  RolodexEntryJson,
  RolodexEntryPatchJson,
  RolodexEntryType,
  RolodexLoadedEntryJson,
} from 'types'
import { GeckoFfsAnnex4bBinningConfiguration } from 'types/entries/geckoFfsAnnex4bBinningConfiguration'

// ----------------------------------------------------------------------------
// Types

export type EntryLookupFunction<T extends GeckoEntry> = (
  key: string | undefined
) => T | undefined

export type RolodexEntryDispenserType = {
  //----------------------------------------------------------------------------

  /** A readonly array of all entries loaded in this query */
  entries: ReadonlyArray<GeckoEntry>
  /** A readonly array of all organizations loaded in this query */
  organizations: ReadonlyArray<GeckoOrganization>
  /** A readonly array of all sites loaded in this query */
  sites: ReadonlyArray<GeckoSite>
  /** A readonly array of all site asset hierarchies loaded in this query */
  siteAssetHierarchies: ReadonlyArray<GeckoSiteAssetHierarchy>
  /** A readonly array of all site configs loaded in this query */
  siteConfigs: ReadonlyArray<GeckoSiteConfig>
  /** A readonly array of all systems loaded in this query */
  systems: ReadonlyArray<GeckoSystem>
  /** A readonly array of all units loaded in this query */
  units: ReadonlyArray<GeckoUnit>
  /** A readonly array of all formatters loaded in this query */
  components: ReadonlyArray<GeckoComponent>
  /** A readonly array of all jobs loaded in this query */
  jobs: ReadonlyArray<GeckoJob>
  /** A readonly array of all inspections loaded in this query */
  inspections: ReadonlyArray<GeckoInspection>
  /** A readonly array of all inspection forms loaded in this query */
  inspectionForms: ReadonlyArray<GeckoInspectionForm>
  /** A readonly array of all visual inspections loaded in this query */
  visualInspections: ReadonlyArray<GeckoVisualInspection>
  /** A readonly array of all visual inspection forms loaded in this query */
  visualInspectionForms: ReadonlyArray<GeckoVisualInspectionForm>
  /** A readonly array of all visual inspection sections loaded in this query */
  visualInspectionSections: ReadonlyArray<GeckoVisualInspectionSection>
  /** A readonly array of all visual inspection fields loaded in this query */
  visualInspectionFields: ReadonlyArray<GeckoVisualInspectionField>
  /** A readonly array of all visual inspection form responses loaded in this query */
  visualInspectionFormResponses: ReadonlyArray<GeckoVisualInspectionFormResponse>
  /** A readonly array of all visual inspection section responses loaded in this query */
  visualInspectionSectionResponses: ReadonlyArray<GeckoVisualInspectionSectionResponse>
  /** A readonly array of all visual inspection field responses loaded in this query */
  visualInspectionFieldResponses: ReadonlyArray<GeckoVisualInspectionFieldResponse>
  /** A readonly array of all reports loaded in this query */
  reports: ReadonlyArray<GeckoReport>
  /** A readonly array of all reporting metadata entries loaded in this query */
  reportingMetadata: ReadonlyArray<GeckoReportingMetadata>
  /** A readonly array of all component data frames (CDFs) loaded in this query */
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>
  /** A readonly array of all component data sets (CDSs) loaded in this query */
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
  /** A readonly array all derived spatial datasets loaded in this query */
  derivedSpatialDatasets: ReadonlyArray<GeckoDerivedSpatialDataset>
  /** A readonly array of all action logs loaded in this query */
  actionLogs: ReadonlyArray<GeckoActionLog>
  /** A readonly array of all alerts loaded in this query */
  alerts: ReadonlyArray<GeckoAlert>
  /** A readonly array of all markup layers loaded in this query */
  markupLayers: ReadonlyArray<GeckoMarkupLayer>
  /** A readonly array of all matterport spaces loaded in this query */
  matterportSpaces: ReadonlyArray<GeckoMatterportSpace>
  /** A readonly array of all the asset models loaded in this query */
  assetModels: ReadonlyArray<GeckoAssetModel>
  /** A readonly array of all component defaults objects in this query */
  componentDefaults: ReadonlyArray<GeckoComponentDefaults>
  /** A readonly array of all the photogrammetry models loaded in this query */
  photogrammetryModels: ReadonlyArray<GeckoPhotogrammetryModel>
  /** A readonly array of all the kestrel models loaded in this query */
  kestrelModels: ReadonlyArray<GeckoKestrelModel>
  /** Detection runs loaded for Kestrel models. */
  kestrelDefectDetectionRuns: ReadonlyArray<GeckoKestrelDefectDetectionRun>
  /** Labeling Studio training-data entries loaded for Kestrel models. */
  kestrelDetectionTrainingData: ReadonlyArray<GeckoKestrelDetectionTrainingData>
  /** A readonly array of all the kestrel-model-unit links loaded in this query */
  kestrelModelUnits: ReadonlyArray<GeckoKestrelModelUnit>
  /** A readonly array of all kestrel model whiteboard entries loaded in this query */
  kestrelModelWhiteboards: ReadonlyArray<GeckoKestrelModelWhiteboard>
  /** A readonly array of all aerospace GVI inspections loaded in this query */
  aerospaceGVIInspections: ReadonlyArray<GeckoAerospaceGVI>
  /** A readonly array of all aerospace GVI camera entries loaded in this query */
  aerospaceGVICameras: ReadonlyArray<GeckoAerospaceGVICameras>
  /** A readonly array of all the site models loaded in this query */
  siteModels: ReadonlyArray<GeckoSiteModel>
  /** A readonly array of all site observations loaded in this query */
  siteObservations: ReadonlyArray<GeckoSiteObservation>
  /** A readonly array of all observation tickets loaded in this query */
  observationTickets: ReadonlyArray<GeckoObservationTicket>
  thirdPartyInspectionDetails: ReadonlyArray<GeckoThirdPartyInspectionDetails>
  potreePointClouds: ReadonlyArray<GeckoPotreePointCloud>
  copcPointClouds: ReadonlyArray<GeckoCopcPointCloud>
  pointCloudScenes: ReadonlyArray<GeckoPointCloudScene>
  geographicPins: ReadonlyArray<GeckoGeographicPin>
  /** A readonly array of all user comments loaded in this query */
  userComments: ReadonlyArray<GeckoUserComment>
  millPlans: ReadonlyArray<GeckoMillPlan>
  /** A readonly array of all Navy production dashboard configs loaded in this query */
  navyProductionDashboardConfigs: ReadonlyArray<GeckoNavyProductionDashboardConfig>
  /** A readonly array of all walkdowns loaded in this query */
  walkdowns: ReadonlyArray<GeckoWalkdown>
  /** A readonly array of all value stories loaded in this query */
  valueStories: ReadonlyArray<GeckoValueStory>
  /** A readonly array of all process flow diagrams loaded in this query */
  processFlowDiagrams: ReadonlyArray<GeckoProcessFlowDiagram>
  /** A readonly array of all risk assessment configs loaded in this query */
  riskAssessmentConfigs: ReadonlyArray<GeckoRiskAssessmentConfig>
  /** A readonly array of all asset health plans loaded in this query */
  assetHealthPlans: ReadonlyArray<GeckoAssetHealthPlan>
  /** A readonly array of all asset health actions loaded in this query */
  assetHealthActions: ReadonlyArray<GeckoAssetHealthAction>
  /** A readonly array of all component deficiencies loaded in this query */
  componentDeficiencies: ReadonlyArray<GeckoComponentDeficiency>
  /** A readonly array of all cases loaded in this query */
  cases: ReadonlyArray<GeckoCase>
  /** A readonly array of all user lists loaded in this query */
  userLists: ReadonlyArray<GeckoUserList>
  /** A readonly array of all navbar configs loaded in this query */
  navbarConfigs: ReadonlyArray<GeckoNavbarConfig>
  /** A readonly array of all homepage configs loaded in this query */
  homepageConfigs: ReadonlyArray<GeckoHomepageConfig>
  /** A readonly array of all lifecycle recommendations loaded in this query */
  lifecycleRecommendations: ReadonlyArray<GeckoLifecycleRecommendation>
  //----------------------------------------------------------------------------

  /** A helper method for grabbing an entry from the query results by ID */
  getEntry: EntryLookupFunction<GeckoEntry>
  /** A helper method for grabbing an organization from the query results by ID */
  getOrganization: EntryLookupFunction<GeckoOrganization>
  /** A helper method for grabbing a site from the query results by ID */
  getSite: EntryLookupFunction<GeckoSite>
  /** A helper method for grabbing a site asset hierarchy from the query results by ID */
  getSiteAssetHierarchy: EntryLookupFunction<GeckoSiteAssetHierarchy>
  /** A helper method for grabbing a site config from the query results by ID */
  getSiteConfig: EntryLookupFunction<GeckoSiteConfig>
  /** A helper method for grabbing a system from the query results by ID */
  getSystem: EntryLookupFunction<GeckoSystem>
  /** A helper method for grabbing a unit from the query results by ID */
  getUnit: EntryLookupFunction<GeckoUnit>
  /** A helper method for grabbing a component from the query results by ID */
  getComponent: EntryLookupFunction<GeckoComponent>
  /** A helper method for grabbing a job from the query results by ID */
  getJob: EntryLookupFunction<GeckoJob>
  /** A helper method for grabbing an inspection from the query results by ID */
  getInspection: EntryLookupFunction<GeckoInspection>
  /** A helper method for grabbing an inspection form from the query results by ID */
  getInspectionForm: EntryLookupFunction<GeckoInspectionForm>
  /** A helper method for grabbing a visual inspection from the query results by ID */
  getVisualInspection: EntryLookupFunction<GeckoVisualInspection>
  /** A helper method for grabbing a visual inspection form from the query results by ID */
  getVisualInspectionForm: EntryLookupFunction<GeckoVisualInspectionForm>
  /** A helper method for grabbing a visual inspection section from the query results by ID */
  getVisualInspectionSection: EntryLookupFunction<GeckoVisualInspectionSection>
  /** A helper method for grabbing a visual inspection field from the query results by ID */
  getVisualInspectionField: EntryLookupFunction<GeckoVisualInspectionField>
  /** A helper method for grabbing a visual inspection form response from the query results by ID */
  getVisualInspectionFormResponse: EntryLookupFunction<GeckoVisualInspectionFormResponse>
  /** A helper method for grabbing a visual inspection section response from the query results by ID */
  getVisualInspectionSectionResponse: EntryLookupFunction<GeckoVisualInspectionSectionResponse>
  /** A helper method for grabbing a visual inspection field response from the query results by ID */
  getVisualInspectionFieldResponse: EntryLookupFunction<GeckoVisualInspectionFieldResponse>
  /** A helper method for grabbing a report from the query results by ID */
  getReport: EntryLookupFunction<GeckoReport>
  /** A helper method for grabbing reporting metadata from the query results by ID */
  getReportingMetadata: EntryLookupFunction<GeckoReportingMetadata>
  /** A helper method for grabbing a component data frame (CDF) from the query results by ID */
  getComponentDataFrame: EntryLookupFunction<GeckoComponentDataFrame>
  /** A helper method for grabbing a component data set (CDS) from the query results by ID */
  getComponentDataSet: EntryLookupFunction<GeckoComponentDataset>
  /** A helper method grabbing derived spatial dataset query results by ID */
  getDerivedSpatialDataset: EntryLookupFunction<GeckoDerivedSpatialDataset>
  /** A helper method for grabbing an action log from the query results by ID */
  getActionLog: EntryLookupFunction<GeckoActionLog>
  /** A helper method for grabbing an alert from the query results by ID */
  getAlert: EntryLookupFunction<GeckoAlert>
  /** A helper method for grabbing a markup layer from the query results by ID */
  getMarkupLayer: EntryLookupFunction<GeckoMarkupLayer>
  /** A helper method for grabbing a matterport space from the query results by ID */
  getMatterportSpace: EntryLookupFunction<GeckoMatterportSpace>
  /** A helper method for grabbing an asset model from the query results by ID */
  getAssetModel: EntryLookupFunction<GeckoAssetModel>
  /** A helper method for grabbing a photogrammetry model from the query results by ID */
  getPhotogrammetryModel: EntryLookupFunction<GeckoPhotogrammetryModel>
  /** A helper method for grabbing a kestrel model from the query results by ID */
  getKestrelModel: EntryLookupFunction<GeckoKestrelModel>
  getKestrelDefectDetectionRun: EntryLookupFunction<GeckoKestrelDefectDetectionRun>
  getKestrelDetectionTrainingData: EntryLookupFunction<GeckoKestrelDetectionTrainingData>
  /** A helper method for grabbing a kestrel-model-unit link from the query results by ID */
  getKestrelModelUnit: EntryLookupFunction<GeckoKestrelModelUnit>
  /** A helper method for grabbing a kestrel model whiteboard from the query results by ID */
  getKestrelModelWhiteboard: EntryLookupFunction<GeckoKestrelModelWhiteboard>
  /** A helper method for grabbing a site observation from the query results by ID */
  getSiteObservation: EntryLookupFunction<GeckoSiteObservation>
  /** A helper method for grabbing an observation ticket from the query results by ID */
  getObservationTicket: EntryLookupFunction<GeckoObservationTicket>
  /** A helper method for grabbing a site model from the query results by ID */
  getSiteModel: EntryLookupFunction<GeckoSiteModel>
  /** A helper method for grabbing a geographic pin from the query results by ID */
  getGeographicPin: EntryLookupFunction<GeckoGeographicPin>
  /** A helper method for grabbing a user comment from the query results by ID */
  getUserComment: EntryLookupFunction<GeckoUserComment>
  /** A helper method for grabbing a mill plan from the query results by ID */
  getMillPlan: EntryLookupFunction<GeckoMillPlan>
  /** A helper method for grabbing a Navy production dashboard config from the query results by ID */
  getNavyProductionDashboardConfig: EntryLookupFunction<GeckoNavyProductionDashboardConfig>
  /** A helper method for grabbing a walkdown from the query results by ID */
  getWalkdown: EntryLookupFunction<GeckoWalkdown>
  /** A helper method for grabbing a value story from the query results by ID */
  getValueStory: EntryLookupFunction<GeckoValueStory>
  /** A helper method for grabbing a process flow diagram from the query results by ID */
  getProcessFlowDiagram: EntryLookupFunction<GeckoProcessFlowDiagram>
  /** A helper method for grabbing a risk assessment config from the query results by ID */
  getRiskAssessmentConfig: EntryLookupFunction<GeckoRiskAssessmentConfig>
  /** A helper method for grabbing an asset health plan from the query results by ID */
  getAssetHealthPlan: EntryLookupFunction<GeckoAssetHealthPlan>
  /** A helper method for grabbing an asset health action from the query results by ID */
  getAssetHealthAction: EntryLookupFunction<GeckoAssetHealthAction>
  /** A helper method for grabbing a component deficiency from the query results by ID */
  getComponentDeficiency: EntryLookupFunction<GeckoComponentDeficiency>
  /** A helper method for grabbing a case from the query results by ID */
  getCase: EntryLookupFunction<GeckoCase>
  /** A helper method for grabbing a user list from the query results by ID */
  getUserList: EntryLookupFunction<GeckoUserList>
  /** A helper method for grabbing an aerospace GVI inspection from the query results by ID */
  getAerospaceGVI: EntryLookupFunction<GeckoAerospaceGVI>
  /** A helper method for grabbing an aerospace GVI cameras entry from the query results by ID */
  getAerospaceGVICameras: EntryLookupFunction<GeckoAerospaceGVICameras>
  /** A helper method for grabbing a lifecycle recommendation from the query results by ID */
  getLifecycleRecommendation: EntryLookupFunction<GeckoLifecycleRecommendation>
}

// ----------------------------------------------------------------------------
// Main Hook

/**
 * A hook for dispensing entries from Rolodex as GeckoEntry objects
 */
export function useDispenseRolodexProperAndCacheEntries(
  rolodexProperFetchData: RolodexLoadedEntryJson[][] | undefined,
  rolodexCacheFetchData:
    | ReadonlyMap<string, RolodexEntryPatchJson | 'deleted'>
    | undefined
): RolodexEntryDispenserType {
  const {
    getEntry: getMatchingEntryFromDispenserCache,
    upsertEntry: addEntryToDispenserCache,
  } = useGetRolodexEntryDispenserCacheInterfaces()

  /**
   * A function that leverages the rolodexEntryDispenserCache to stabilize the returned class-object references.
   */
  const getOrCreateClassObjectWithStableReference = useCallback(
    (entryJson: RolodexEntryJson) => {
      const cachedEntry = getMatchingEntryFromDispenserCache(entryJson)
      if (cachedEntry) return cachedEntry

      const newClassObj = createClassObjForEntryJson(entryJson)
      if (!newClassObj) return undefined
      addEntryToDispenserCache(newClassObj)
      return newClassObj
    },
    [getMatchingEntryFromDispenserCache, addEntryToDispenserCache]
  )

  //use query param feature flag, pass through to createDispenserFromGeckoEntryArray()
  const rolodexProperEntriesAsForecastClass = React.useMemo((): {
    id: string
    classObj: GeckoEntry
    index: number
    json: RolodexEntryJson
  }[] => {
    const newSoftDeletedEntryIDs = new Set<string>()

    const cdsProvidedLayerKeys = collectComponentDatasetLayerKeys(
      rolodexProperFetchData
    )

    // TODO ENT-1028: is this (index) really the best way to preserve sorting order?
    const allEntriesAsClassObjs: {
      id: string
      classObj: GeckoEntry
      index: number
      json: RolodexEntryJson
    }[] = []
    rolodexProperFetchData?.forEach((arr) =>
      arr.forEach(({ entry, links }) => {
        if (shouldDropProperComponentDataFrame(entry, cdsProvidedLayerKeys)) {
          return
        }

        if (
          entry.type === RolodexEntryType.COMPONENT_DATASET &&
          (entry as GeckoComponentDataset).data.schema_version <
            MINIMUM_CDS_SCHEMA_VERSION
        ) {
          return
        }
        const entryAsClassObj = getOrCreateClassObjectWithStableReference(entry)
        if (isNil(entryAsClassObj)) {
          return
        }

        allEntriesAsClassObjs.push({
          id: entry.id,
          classObj: entryAsClassObj,
          index: allEntriesAsClassObjs.length,
          json: entry,
        })

        // if this is a CDS and we are fetching from parquet, create CDFs from it
        if (
          entryAsClassObj instanceof GeckoComponentDataset &&
          GeckoComponentDataset.AreTagsValid(entryAsClassObj)
        ) {
          const cdfEntries =
            createComponentDataFrameEntryJsonsFromComponentDataset(
              entryAsClassObj
            )

          cdfEntries.forEach((cdf) => {
            const classObject = getOrCreateClassObjectWithStableReference(cdf)
            if (!classObject) {
              throw new Error(
                `Invalid CDF (id=${cdf.id}) generated from CDS (id=${entry.id})`
              )
            }
            allEntriesAsClassObjs.push({
              id: cdf.id,
              classObj: classObject,
              index: allEntriesAsClassObjs.length,
              json: cdf,
            })
          })
        }

        // TODO ENT-1027: bump the generation here somehow
        if (links) {
          Object.values(links).forEach((linkedEntry) => {
            // TODO ENT-1028: Totally wrong, as far as sorting order is concerned!
            const linkedEntryAsClassObj =
              getOrCreateClassObjectWithStableReference(linkedEntry)
            if (isNil(linkedEntryAsClassObj)) {
              return
            }
            allEntriesAsClassObjs.push({
              id: linkedEntry.id,
              classObj: linkedEntryAsClassObj,
              index: allEntriesAsClassObjs.length,
              json: linkedEntry,
            })
          })
        }
      })
    )

    if (newSoftDeletedEntryIDs.size > 0)
      console.warn(
        `Soft deleted entries:`,
        Array.from(newSoftDeletedEntryIDs.values())
      )

    return allEntriesAsClassObjs
  }, [getOrCreateClassObjectWithStableReference, rolodexProperFetchData])

  const rolodexCacheEntriesAsForecastClass = React.useMemo((): {
    id: string
    classObj: GeckoEntry | 'deleted'
    index: number
    json: RolodexEntryJson | 'deleted'
  }[] => {
    const allEntriesAsClassObjs: {
      id: string
      classObj: GeckoEntry | 'deleted'
      index: number
      json: RolodexEntryJson | 'deleted'
    }[] = []
    if (rolodexCacheFetchData) {
      rolodexCacheFetchData.forEach((entry, id) => {
        if (entry === 'deleted') {
          allEntriesAsClassObjs.push({
            id,
            classObj: 'deleted',
            index: allEntriesAsClassObjs.length,
            json: 'deleted',
          })
          return
        }
        const patchedJSON = {
          id: id,
          isFromCache: true,
        }
        merge(patchedJSON, entry)
        const linkedEntryAsClassObj = getOrCreateClassObjectWithStableReference(
          patchedJSON as CacheableRolodexEntryJson
        )
        if (linkedEntryAsClassObj) {
          allEntriesAsClassObjs.push({
            id,
            classObj: linkedEntryAsClassObj,
            index: allEntriesAsClassObjs.length,
            json: patchedJSON as CacheableRolodexEntryJson,
          })
        }
      })
    }
    return allEntriesAsClassObjs
  }, [getOrCreateClassObjectWithStableReference, rolodexCacheFetchData])

  // ----------------------------------------------------------------------------
  // Merge entry lists

  return React.useMemo<RolodexEntryDispenserType>(() => {
    const map = new Map<
      string,
      { classObj: GeckoEntry; index: number; json: RolodexEntryJson }
    >()
    rolodexProperEntriesAsForecastClass.forEach((entryProperties) => {
      map.set(entryProperties.id, entryProperties)
    })

    rolodexCacheEntriesAsForecastClass.forEach(
      ({ classObj, json, id, index }) => {
        if (json === 'deleted' || classObj === 'deleted') {
          map.delete(id)
          return
        }
        const rolodexEntryWithId = map.get(id)
        // If there is an entry in Rolodex-Proper with the same ID, apply the cache entry to it using lodash.merge if possible
        if (rolodexEntryWithId) {
          const mergedEntry = {}
          merge(mergedEntry, rolodexEntryWithId.json, json)
          if (isEqual(mergedEntry, json)) {
            // if the entries are the same, keep the original classObj as to not create a new reference
            map.set(id, { classObj, index, json })
            return
          }

          const newClassObj = getOrCreateClassObjectWithStableReference(
            mergedEntry as CacheableRolodexEntryJson
          )
          if (isNil(newClassObj)) {
            // this was soft-deleted in proper, but a cache entry exists
            map.set(id, { classObj, json, index })
            return
          }

          map.set(id, {
            classObj: newClassObj,
            index,
            json: mergedEntry as CacheableRolodexEntryJson,
          })
          return
        }

        // If there is no entry in Rolodex-Proper with the same ID, add the cache entry as a new entry
        map.set(id, { classObj, index, json })
        return
      }
    )

    const classObjArray = Array.from(map.values())
      .sort(({ index: indexA }, { index: indexB }) => indexA - indexB)
      .map(({ classObj }) => classObj)

    return createDispenserFromGeckoEntryArray(classObjArray)
  }, [
    getOrCreateClassObjectWithStableReference,
    rolodexCacheEntriesAsForecastClass,
    rolodexProperEntriesAsForecastClass,
  ])
}

// ----------------------------------------------------------------------------
// Helper Functions

export function createClassObjForEntryJson(
  json: CacheableRolodexEntryJson
): GeckoEntry | undefined {
  try {
    const entryType = json.type
    switch (entryType) {
      case 'organization':
        return GeckoOrganization.CreateFromJSON(json)
      case RolodexEntryType.SITE:
        return GeckoSite.CreateFromJSON(
          json as RolodexEntryJson<RolodexEntryType.SITE>
        )
      case 'site-config':
        return GeckoSiteConfig.CreateFromJSON(json)
      case RolodexEntryType.SITE_ASSET_HIERARCHY:
        return GeckoSiteAssetHierarchy.CreateFromJSON(json)
      case 'system':
        return GeckoSystem.CreateFromJSON(json)
      case RolodexEntryType.RISK_ASSESSMENT_CONFIG:
        return GeckoRiskAssessmentConfig.CreateFromJSON(json)
      case 'unit':
        return GeckoUnit.CreateFromJSON(json)
      case 'component':
        return GeckoComponent.CreateFromJSON(json)
      case 'job':
        return GeckoJob.CreateFromJSON(json)
      case 'inspection':
        return GeckoInspection.CreateFromJSON(json)
      case RolodexEntryType.INSPECTION_FORM:
        return GeckoInspectionForm.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION:
        return GeckoVisualInspection.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_FORM:
        return GeckoVisualInspectionForm.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_SECTION:
        return GeckoVisualInspectionSection.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_FIELD:
        return GeckoVisualInspectionField.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE:
        return GeckoVisualInspectionFormResponse.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_SECTION_RESPONSE:
        return GeckoVisualInspectionSectionResponse.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_FIELD_RESPONSE:
        return GeckoVisualInspectionFieldResponse.CreateFromJSON(json)
      case 'report':
        return GeckoReport.CreateFromJSON(json)
      case RolodexEntryType.REPORTING_METADATA:
        return GeckoReportingMetadata.CreateFromJSON(json)
      case 'component-dataset':
        return GeckoComponentDataset.CreateFromJSON(json)
      case RolodexEntryType.DERIVED_SPATIAL_DATASET:
        return GeckoDerivedSpatialDataset.CreateFromJSON(json)
      case 'action-log':
        return GeckoActionLog.CreateFromJSON(json)
      case RolodexEntryType.COMPONENT_DATA_FRAME:
      case RolodexEntryType.FDE_COMPONENT_DATA_FRAME:
        return GeckoComponentDataFrame.CreateFromJSON(json)
      case 'markup-layer':
        return GeckoMarkupLayer.CreateFromJSON(json)
      case RolodexEntryType.MATTERPORT_SPACE:
        return GeckoMatterportSpace.CreateFromJSON(json)
      case 'asset-model':
        return createGeckoAssetModel(json)
      case 'component-defaults':
        return GeckoComponentDefaults.CreateFromJSON(json)
      case 'photogrammetry-model':
        return GeckoPhotogrammetryModel.CreateFromJSON(json)
      case RolodexEntryType.KESTREL_MODEL:
        return GeckoKestrelModel.CreateFromJSON(json)
      case RolodexEntryType.KESTREL_DEFECT_DETECTION_RUN:
        return GeckoKestrelDefectDetectionRun.CreateFromJSON(json)
      case RolodexEntryType.KESTREL_DETECTION_TRAINING_DATA:
        return GeckoKestrelDetectionTrainingData.CreateFromJSON(json)
      case RolodexEntryType.AEROSPACE_GVI:
        return GeckoAerospaceGVI.CreateFromJSON(json)
      case RolodexEntryType.AEROSPACE_GVI_CAMERAS:
        return GeckoAerospaceGVICameras.CreateFromJSON(json)
      case RolodexEntryType.KESTREL_MODEL_CAMERAS:
        return GeckoKestrelModelCameras.CreateFromJSON(json)
      case RolodexEntryType.KESTREL_MODEL_UNIT:
        return GeckoKestrelModelUnit.CreateFromJSON(json)
      case RolodexEntryType.KESTREL_MODEL_WHITEBOARD:
        return GeckoKestrelModelWhiteboard.CreateFromJSON(json)
      case 'third-party-inspection-details':
        return GeckoThirdPartyInspectionDetails.CreateFromJSON(json)
      case RolodexEntryType.POTREE_POINT_CLOUD:
        return GeckoPotreePointCloud.CreateFromJSON(json)
      case RolodexEntryType.COPC_POINT_CLOUD:
        return GeckoCopcPointCloud.CreateFromJSON(json)
      case RolodexEntryType.POINT_CLOUD_SCENE:
        return GeckoPointCloudScene.CreateFromJSON(json)
      case RolodexEntryType.OBSERVATION:
        return GeckoSiteObservation.CreateFromJSON(json)
      case RolodexEntryType.OBSERVATION_TICKET:
        return GeckoObservationTicket.CreateFromJSON(json)
      case 'site-model':
        return GeckoSiteModel.CreateFromJSON(json)
      case RolodexEntryType.GEOGRAPHIC_PIN:
        return GeckoGeographicPin.CreateFromJSON(json)
      case RolodexEntryType.USER_COMMENT:
        return GeckoUserComment.CreateFromJSON(json)
      case RolodexEntryType.NAVY_PRODUCTION_DASHBOARD_CONFIG:
        return GeckoNavyProductionDashboardConfig.CreateFromJSON(json)
      case RolodexEntryType.WALKDOWN:
        return GeckoWalkdown.CreateFromJSON(json)
      case RolodexEntryType.PROCESS_FLOW_DIAGRAM:
        return GeckoProcessFlowDiagram.CreateFromJSON(json)
      case RolodexEntryType.COMPONENT_HEALTH_PLAN:
        return GeckoAssetHealthPlan.CreateFromJSON(json)
      case RolodexEntryType.COMPONENT_HEALTH_PLAN_ACTION:
        return GeckoAssetHealthAction.CreateFromJSON(json)
      case RolodexEntryType.COMPONENT_DEFICIENCY:
        return GeckoComponentDeficiency.CreateFromJSON(json)
      case RolodexEntryType.CASE:
        return new GeckoCase(json)
      case RolodexEntryType.USER_LIST:
        return GeckoUserList.FromJSON(json)
      case RolodexEntryType.ALERT:
        return new GeckoAlert(json)
      case RolodexEntryType.NAVBAR_CONFIG:
        return GeckoNavbarConfig.CreateFromJSON(json)
      case RolodexEntryType.HOMEPAGE_CONFIG:
        return GeckoHomepageConfig.CreateFromJSON(json)
      case RolodexEntryType.LIFECYCLE_RECOMMENDATION:
        return GeckoLifecycleRecommendation.CreateFromJSON(json)
      case RolodexEntryType.RISK_SCENARIO:
        return GeckoRiskScenario.CreateFromJSON(json)
      case RolodexEntryType.RISK_GATE_0_FORM:
        return GeckoRiskGate0Form.CreateFromJSON(json)
      case RolodexEntryType.DOCUMENT_TIMELINE_ENTRY:
        return GeckoDocumentTimelineEntry.CreateFromJSON(json)
      case RolodexEntryType.FFS_CONFIGURATION:
        return GeckoFfsAnnex4bBinningConfiguration.CreateFromJSON(json)
      default:
        return {
          id: json.id,
          type: json.type,
          data: { ...json.data },
          tags: { ...json.tags },
          index: { ...json.index },
          raw: json,
          generation: json.generation,
          updated_at: json.updated_at,
          isFromCache: json.isFromCache ?? false,
          access_restriction:
            json.access_restriction === 'internal' ? 'internal' : null,
        }
    }
  } catch (error) {
    console.warn(`Failed to create object for "${json.id}"`, error)
    return undefined
  }
}

function createDispenserFromGeckoEntryArray(
  entryClassObjs: GeckoEntry[]
): RolodexEntryDispenserType {
  const allEntriesMap = new Map<string | undefined, GeckoEntry>()
  const organizationMap = new Map<string | undefined, GeckoOrganization>()
  const siteMap = new Map<string | undefined, GeckoSite>()
  const siteAssetHierarchyMap = new Map<
    string | undefined,
    GeckoSiteAssetHierarchy
  >()
  const siteConfigMap = new Map<string | undefined, GeckoSiteConfig>()
  const systemMap = new Map<string | undefined, GeckoSystem>()
  const unitMap = new Map<string | undefined, GeckoUnit>()
  const componentMap = new Map<string | undefined, GeckoComponent>()
  const jobMap = new Map<string | undefined, GeckoJob>()
  const inspectionMap = new Map<string | undefined, GeckoInspection>()
  const inspectionFormMap = new Map<string | undefined, GeckoInspectionForm>()
  const visualInspectionMap = new Map<
    string | undefined,
    GeckoVisualInspection
  >()
  const visualInspectionFormMap = new Map<
    string | undefined,
    GeckoVisualInspectionForm
  >()
  const visualInspectionSectionMap = new Map<
    string | undefined,
    GeckoVisualInspectionSection
  >()
  const visualInspectionFieldMap = new Map<
    string | undefined,
    GeckoVisualInspectionField
  >()
  const visualInspectionFormResponseMap = new Map<
    string | undefined,
    GeckoVisualInspectionFormResponse
  >()
  const visualInspectionSectionResponseMap = new Map<
    string | undefined,
    GeckoVisualInspectionSectionResponse
  >()
  const visualInspectionFieldResponseMap = new Map<
    string | undefined,
    GeckoVisualInspectionFieldResponse
  >()
  const reportMap = new Map<string | undefined, GeckoReport>()
  const reportingMetadataMap = new Map<
    string | undefined,
    GeckoReportingMetadata
  >()
  const componentDataFrameMap = new Map<
    string | undefined,
    GeckoComponentDataFrame
  >()
  const componentDataSetMap = new Map<
    string | undefined,
    GeckoComponentDataset
  >()
  const derivedSpatialDatasetMap = new Map<
    string | undefined,
    GeckoDerivedSpatialDataset
  >()
  const actionLogMap = new Map<string | undefined, GeckoActionLog>()
  const alertMap = new Map<string | undefined, GeckoAlert>()
  const componentDefaultsMap = new Map<
    string | undefined,
    GeckoComponentDefaults
  >()
  const markupLayerMap = new Map<string | undefined, GeckoMarkupLayer>()
  const matterportSpaceMap = new Map<string | undefined, GeckoMatterportSpace>()
  const assetModelMap = new Map<string | undefined, GeckoAssetModel>()
  const photogrammetryModelMap = new Map<
    string | undefined,
    GeckoPhotogrammetryModel
  >()
  const kestrelModelMap = new Map<string | undefined, GeckoKestrelModel>()
  const kestrelDefectDetectionRunMap = new Map<
    string | undefined,
    GeckoKestrelDefectDetectionRun
  >()
  const kestrelDetectionTrainingDataMap = new Map<
    string | undefined,
    GeckoKestrelDetectionTrainingData
  >()
  const kestrelModelUnitMap = new Map<
    string | undefined,
    GeckoKestrelModelUnit
  >()
  const kestrelModelWhiteboardMap = new Map<
    string | undefined,
    GeckoKestrelModelWhiteboard
  >()
  const aerospaceGVIMap = new Map<string | undefined, GeckoAerospaceGVI>()
  const aerospaceGVICamerasMap = new Map<
    string | undefined,
    GeckoAerospaceGVICameras
  >()
  const thirdPartyInspectionDetailsMap = new Map<
    string | undefined,
    GeckoThirdPartyInspectionDetails
  >()
  const potreePointCloudMap = new Map<
    string | undefined,
    GeckoPotreePointCloud
  >()
  const copcPointCloudMap = new Map<string | undefined, GeckoCopcPointCloud>()
  const pointCloudSceneMap = new Map<string | undefined, GeckoPointCloudScene>()
  const siteObservationMap = new Map<string | undefined, GeckoSiteObservation>()
  const observationTicketMap = new Map<
    string | undefined,
    GeckoObservationTicket
  >()
  const siteModelMap = new Map<string | undefined, GeckoSiteModel>()
  const geographicPinMap = new Map<string | undefined, GeckoGeographicPin>()
  const userCommentMap = new Map<string | undefined, GeckoUserComment>()
  const millPlanMap = new Map<string | undefined, GeckoMillPlan>()
  const navyProductionDashboardConfigMap = new Map<
    string | undefined,
    GeckoNavyProductionDashboardConfig
  >()
  const walkdownMap = new Map<string | undefined, GeckoWalkdown>()
  const valueStoryMap = new Map<string | undefined, GeckoValueStory>()
  const processFlowDiagramMap = new Map<
    string | undefined,
    GeckoProcessFlowDiagram
  >()
  const riskAssessmentConfigMap = new Map<
    string | undefined,
    GeckoRiskAssessmentConfig
  >()
  const assetHealthPlanMap = new Map<string | undefined, GeckoAssetHealthPlan>()
  const assetHealthActionMap = new Map<
    string | undefined,
    GeckoAssetHealthAction
  >()
  const componentDeficiencyMap = new Map<
    string | undefined,
    GeckoComponentDeficiency
  >()
  const casesMap = new Map<string | undefined, GeckoCase>()
  const userListMap = new Map<string | undefined, GeckoUserList>()
  const navbarConfigMap = new Map<string | undefined, GeckoNavbarConfig>()
  const homepageConfigMap = new Map<string | undefined, GeckoHomepageConfig>()
  const lifecycleRecommendationMap = new Map<
    string | undefined,
    GeckoLifecycleRecommendation
  >()

  // Sort into maps
  entryClassObjs.forEach((entry) => {
    switch (entry.type) {
      case 'organization':
        organizationMap.set(entry.id, entry as GeckoOrganization)
        break
      case 'site':
        siteMap.set(entry.id, entry as GeckoSite)
        break
      case RolodexEntryType.SITE_ASSET_HIERARCHY:
        siteAssetHierarchyMap.set(entry.id, entry as GeckoSiteAssetHierarchy)
        break
      case 'site-config':
        siteConfigMap.set(entry.id, entry as GeckoSiteConfig)
        break
      case 'unit':
        unitMap.set(entry.id, entry as GeckoUnit)
        break
      case 'component':
        componentMap.set(entry.id, entry as GeckoComponent)
        break
      case 'job':
        jobMap.set(entry.id, entry as GeckoJob)
        break
      case 'inspection':
        inspectionMap.set(entry.id, entry as GeckoInspection)
        break
      case RolodexEntryType.INSPECTION_FORM:
        inspectionFormMap.set(entry.id, entry as GeckoInspectionForm)
        break
      case RolodexEntryType.VISUAL_INSPECTION:
        visualInspectionMap.set(entry.id, entry as GeckoVisualInspection)
        break
      case RolodexEntryType.VISUAL_INSPECTION_FORM:
        visualInspectionFormMap.set(
          entry.id,
          entry as GeckoVisualInspectionForm
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_SECTION:
        visualInspectionSectionMap.set(
          entry.id,
          entry as GeckoVisualInspectionSection
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_FIELD:
        visualInspectionFieldMap.set(
          entry.id,
          entry as GeckoVisualInspectionField
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE:
        visualInspectionFormResponseMap.set(
          entry.id,
          entry as GeckoVisualInspectionFormResponse
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_SECTION_RESPONSE:
        visualInspectionSectionResponseMap.set(
          entry.id,
          entry as GeckoVisualInspectionSectionResponse
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_FIELD_RESPONSE:
        visualInspectionFieldResponseMap.set(
          entry.id,
          entry as GeckoVisualInspectionFieldResponse
        )
        break
      case 'report':
        reportMap.set(entry.id, entry as GeckoReport)
        break
      case RolodexEntryType.REPORTING_METADATA:
        reportingMetadataMap.set(entry.id, entry as GeckoReportingMetadata)
        break
      case 'component-dataset':
        componentDataSetMap.set(entry.id, entry as GeckoComponentDataset)
        break
      case RolodexEntryType.DERIVED_SPATIAL_DATASET:
        derivedSpatialDatasetMap.set(
          entry.id,
          entry as GeckoDerivedSpatialDataset
        )
        break
      case 'action-log':
        actionLogMap.set(entry.id, entry as GeckoActionLog)
        break
      case RolodexEntryType.ALERT:
        alertMap.set(entry.id, entry as GeckoAlert)
        break
      case RolodexEntryType.COMPONENT_DATA_FRAME:
      case RolodexEntryType.FDE_COMPONENT_DATA_FRAME:
        componentDataFrameMap.set(entry.id, entry as GeckoComponentDataFrame)
        break
      case 'markup-layer':
        markupLayerMap.set(entry.id, entry as GeckoMarkupLayer)
        break
      case RolodexEntryType.MATTERPORT_SPACE:
        matterportSpaceMap.set(entry.id, entry as GeckoMatterportSpace)
        break
      case 'asset-model':
        assetModelMap.set(entry.id, getAssetModelType(entry))
        break
      case 'component-defaults':
        componentDefaultsMap.set(entry.id, entry as GeckoComponentDefaults)
        break
      case 'photogrammetry-model':
        photogrammetryModelMap.set(entry.id, entry as GeckoPhotogrammetryModel)
        break
      case 'kestrel-model':
        kestrelModelMap.set(entry.id, entry as GeckoKestrelModel)
        break
      case RolodexEntryType.KESTREL_DEFECT_DETECTION_RUN:
        kestrelDefectDetectionRunMap.set(
          entry.id,
          entry as GeckoKestrelDefectDetectionRun
        )
        break
      case RolodexEntryType.KESTREL_DETECTION_TRAINING_DATA:
        kestrelDetectionTrainingDataMap.set(
          entry.id,
          entry as GeckoKestrelDetectionTrainingData
        )
        break
      case RolodexEntryType.KESTREL_MODEL_UNIT:
        kestrelModelUnitMap.set(entry.id, entry as GeckoKestrelModelUnit)
        break
      case RolodexEntryType.KESTREL_MODEL_WHITEBOARD:
        kestrelModelWhiteboardMap.set(
          entry.id,
          entry as GeckoKestrelModelWhiteboard
        )
        break
      case RolodexEntryType.AEROSPACE_GVI:
        aerospaceGVIMap.set(entry.id, entry as GeckoAerospaceGVI)
        break
      case RolodexEntryType.AEROSPACE_GVI_CAMERAS:
        aerospaceGVICamerasMap.set(entry.id, entry as GeckoAerospaceGVICameras)
        break
      case 'third-party-inspection-details':
        thirdPartyInspectionDetailsMap.set(
          entry.id,
          entry as GeckoThirdPartyInspectionDetails
        )
        break
      case RolodexEntryType.POTREE_POINT_CLOUD:
        potreePointCloudMap.set(entry.id, entry as GeckoPotreePointCloud)
        break
      case RolodexEntryType.COPC_POINT_CLOUD:
        copcPointCloudMap.set(entry.id, entry as GeckoCopcPointCloud)
        break
      case RolodexEntryType.POINT_CLOUD_SCENE:
        pointCloudSceneMap.set(entry.id, entry as GeckoPointCloudScene)
        break
      case RolodexEntryType.OBSERVATION:
        siteObservationMap.set(entry.id, entry as GeckoSiteObservation)
        break
      case RolodexEntryType.OBSERVATION_TICKET:
        observationTicketMap.set(entry.id, entry as GeckoObservationTicket)
        break
      case 'site-model':
        siteModelMap.set(entry.id, entry as GeckoSiteModel)
        break
      case RolodexEntryType.GEOGRAPHIC_PIN:
        geographicPinMap.set(entry.id, entry as GeckoGeographicPin)
        break
      case RolodexEntryType.USER_COMMENT:
        userCommentMap.set(entry.id, entry as GeckoUserComment)
        break
      case RolodexEntryType.MILL_PLAN:
        millPlanMap.set(entry.id, entry as GeckoMillPlan)
        break
      case RolodexEntryType.NAVY_PRODUCTION_DASHBOARD_CONFIG:
        navyProductionDashboardConfigMap.set(
          entry.id,
          entry as GeckoNavyProductionDashboardConfig
        )
        break
      case RolodexEntryType.WALKDOWN:
        walkdownMap.set(entry.id, entry as GeckoWalkdown)
        break
      case RolodexEntryType.VALUE_STORY:
        valueStoryMap.set(entry.id, entry as GeckoValueStory)
        break
      case RolodexEntryType.PROCESS_FLOW_DIAGRAM:
        processFlowDiagramMap.set(entry.id, entry as GeckoProcessFlowDiagram)
        break
      case RolodexEntryType.SYSTEM:
        systemMap.set(entry.id, entry as GeckoSystem)
        break
      case RolodexEntryType.RISK_ASSESSMENT_CONFIG:
        riskAssessmentConfigMap.set(
          entry.id,
          entry as GeckoRiskAssessmentConfig
        )
        break
      case RolodexEntryType.COMPONENT_HEALTH_PLAN:
        assetHealthPlanMap.set(entry.id, entry as GeckoAssetHealthPlan)
        break
      case RolodexEntryType.COMPONENT_HEALTH_PLAN_ACTION:
        assetHealthActionMap.set(entry.id, entry as GeckoAssetHealthAction)
        break
      case RolodexEntryType.COMPONENT_DEFICIENCY:
        componentDeficiencyMap.set(entry.id, entry as GeckoComponentDeficiency)
        break
      case RolodexEntryType.CASE:
        casesMap.set(entry.id, entry as GeckoCase)
        break
      case RolodexEntryType.USER_LIST:
        userListMap.set(entry.id, entry as GeckoUserList)
        break
      case RolodexEntryType.NAVBAR_CONFIG:
        navbarConfigMap.set(entry.id, entry as GeckoNavbarConfig)
        break
      case RolodexEntryType.HOMEPAGE_CONFIG:
        homepageConfigMap.set(entry.id, entry as GeckoHomepageConfig)
        break
      case RolodexEntryType.LIFECYCLE_RECOMMENDATION:
        lifecycleRecommendationMap.set(
          entry.id,
          entry as GeckoLifecycleRecommendation
        )
        break
      default:
        break
    }
    allEntriesMap.set(entry.id, entry)
  })

  const entries = Array.from(allEntriesMap.values())
  const organizations = Array.from(organizationMap.values())
  const sites = Array.from(siteMap.values())
  const siteAssetHierarchies = Array.from(siteAssetHierarchyMap.values())
  const siteConfigs = Array.from(siteConfigMap.values())
  const systems = Array.from(systemMap.values())
  const units = Array.from(unitMap.values())
  const components = Array.from(componentMap.values())
  const jobs = Array.from(jobMap.values())
  const inspections = Array.from(inspectionMap.values())
  const inspectionForms = Array.from(inspectionFormMap.values())
  const visualInspections = Array.from(visualInspectionMap.values())
  const visualInspectionForms = Array.from(visualInspectionFormMap.values())
  const visualInspectionSections = Array.from(
    visualInspectionSectionMap.values()
  )
  const visualInspectionFields = Array.from(visualInspectionFieldMap.values())
  const visualInspectionFormResponses = Array.from(
    visualInspectionFormResponseMap.values()
  )
  const visualInspectionSectionResponses = Array.from(
    visualInspectionSectionResponseMap.values()
  )
  const visualInspectionFieldResponses = Array.from(
    visualInspectionFieldResponseMap.values()
  )
  const reports = Array.from(reportMap.values())
  const reportingMetadata = Array.from(reportingMetadataMap.values())
  const componentDataFrames = Array.from(componentDataFrameMap.values())
  const componentDatasets = Array.from(componentDataSetMap.values())
  const derivedSpatialDatasets = Array.from(derivedSpatialDatasetMap.values())
  const actionLogs = Array.from(actionLogMap.values())
  const alerts = Array.from(alertMap.values())
  const componentDefaults = Array.from(componentDefaultsMap.values())
  const markupLayers = Array.from(markupLayerMap.values())
  const matterportSpaces = Array.from(matterportSpaceMap.values())
  const assetModels = Array.from(assetModelMap.values())
  const photogrammetryModels = Array.from(photogrammetryModelMap.values())
  const kestrelModels = Array.from(kestrelModelMap.values())
  const kestrelDefectDetectionRuns = Array.from(
    kestrelDefectDetectionRunMap.values()
  )
  const kestrelDetectionTrainingData = Array.from(
    kestrelDetectionTrainingDataMap.values()
  )
  const kestrelModelUnits = Array.from(kestrelModelUnitMap.values())
  const kestrelModelWhiteboards = Array.from(kestrelModelWhiteboardMap.values())
  const aerospaceGVIInspections = Array.from(aerospaceGVIMap.values())
  const aerospaceGVICameras = Array.from(aerospaceGVICamerasMap.values())
  const thirdPartyInspectionDetails = Array.from(
    thirdPartyInspectionDetailsMap.values()
  )
  const potreePointClouds = Array.from(potreePointCloudMap.values())
  const copcPointClouds = Array.from(copcPointCloudMap.values())
  const pointCloudScenes = Array.from(pointCloudSceneMap.values())
  const siteObservations = Array.from(siteObservationMap.values())
  const observationTickets = Array.from(observationTicketMap.values())
  const siteModels = Array.from(siteModelMap.values())
  const geographicPins = Array.from(geographicPinMap.values())
  const userComments = Array.from(userCommentMap.values())
  const millPlans = Array.from(millPlanMap.values())
  const navyProductionDashboardConfigs = Array.from(
    navyProductionDashboardConfigMap.values()
  )
  const walkdowns = Array.from(walkdownMap.values())
  const valueStories = Array.from(valueStoryMap.values())
  const processFlowDiagrams = Array.from(processFlowDiagramMap.values())
  const riskAssessmentConfigs = Array.from(riskAssessmentConfigMap.values())
  const assetHealthPlans = Array.from(assetHealthPlanMap.values())
  const assetHealthActions = Array.from(assetHealthActionMap.values())
  const componentDeficiencies = Array.from(componentDeficiencyMap.values())
  const cases = Array.from(casesMap.values())
  const userLists = Array.from(userListMap.values())
  const navbarConfigs = Array.from(navbarConfigMap.values())
  const homepageConfigs = Array.from(homepageConfigMap.values())
  const lifecycleRecommendations = Array.from(
    lifecycleRecommendationMap.values()
  )

  return {
    // Entry Arrays
    entries,
    organizations,
    sites,
    siteAssetHierarchies,
    siteConfigs,
    systems,
    units,
    components,
    jobs,
    inspections,
    inspectionForms,
    reports,
    reportingMetadata,
    componentDataFrames,
    componentDatasets,
    derivedSpatialDatasets,
    actionLogs,
    alerts,
    markupLayers,
    matterportSpaces,
    assetModels,
    componentDefaults,
    photogrammetryModels,
    kestrelModels,
    kestrelDefectDetectionRuns,
    kestrelDetectionTrainingData,
    kestrelModelUnits,
    kestrelModelWhiteboards,
    aerospaceGVIInspections,
    aerospaceGVICameras,
    thirdPartyInspectionDetails,
    potreePointClouds,
    copcPointClouds,
    pointCloudScenes,
    siteObservations,
    observationTickets,
    siteModels,
    geographicPins,
    visualInspections,
    visualInspectionForms,
    visualInspectionSections,
    visualInspectionFields,
    visualInspectionFormResponses,
    visualInspectionSectionResponses,
    visualInspectionFieldResponses,
    userComments,
    millPlans,
    navyProductionDashboardConfigs,
    walkdowns,
    valueStories,
    processFlowDiagrams,
    riskAssessmentConfigs,
    assetHealthPlans,
    assetHealthActions,
    componentDeficiencies,
    cases,
    userLists,
    navbarConfigs,
    homepageConfigs,
    lifecycleRecommendations,
    // Map accessors
    getEntry: (id) => allEntriesMap.get(id),
    getOrganization: (id) => organizationMap.get(id),
    getSite: (id) => siteMap.get(id),
    getSiteAssetHierarchy: (id) => siteAssetHierarchyMap.get(id),
    getSiteConfig: (id) => siteConfigMap.get(id),
    getSystem: (id) => systemMap.get(id),
    getUnit: (id) => unitMap.get(id),
    getComponent: (id) => componentMap.get(id),
    getJob: (id) => jobMap.get(id),
    getInspection: (id) => inspectionMap.get(id),
    getInspectionForm: (id) => inspectionFormMap.get(id),
    getReport: (id) => reportMap.get(id),
    getReportingMetadata: (id) => reportingMetadataMap.get(id),
    getComponentDataFrame: (id) => componentDataFrameMap.get(id),
    getComponentDataSet: (id) => componentDataSetMap.get(id),
    getDerivedSpatialDataset: (id) => derivedSpatialDatasetMap.get(id),
    getActionLog: (id) => actionLogMap.get(id),
    getAlert: (id) => alertMap.get(id),
    getMarkupLayer: (id) => markupLayerMap.get(id),
    getMatterportSpace: (id) => matterportSpaceMap.get(id),
    getAssetModel: (id) => assetModelMap.get(id),
    getPhotogrammetryModel: (id) => photogrammetryModelMap.get(id),
    getKestrelModel: (id) => kestrelModelMap.get(id),
    getKestrelDefectDetectionRun: (id) => kestrelDefectDetectionRunMap.get(id),
    getKestrelDetectionTrainingData: (id) =>
      kestrelDetectionTrainingDataMap.get(id),
    getKestrelModelUnit: (id) => kestrelModelUnitMap.get(id),
    getKestrelModelWhiteboard: (id) => kestrelModelWhiteboardMap.get(id),
    getSiteObservation: (id) => siteObservationMap.get(id),
    getObservationTicket: (id) => observationTicketMap.get(id),
    getSiteModel: (id) => siteModelMap.get(id),
    getGeographicPin: (id) => geographicPinMap.get(id),
    getVisualInspection: (id) => visualInspectionMap.get(id),
    getVisualInspectionForm: (id) => visualInspectionFormMap.get(id),
    getVisualInspectionSection: (id) => visualInspectionSectionMap.get(id),
    getVisualInspectionField: (id) => visualInspectionFieldMap.get(id),
    getVisualInspectionFormResponse: (id) =>
      visualInspectionFormResponseMap.get(id),
    getVisualInspectionSectionResponse: (id) =>
      visualInspectionSectionResponseMap.get(id),
    getVisualInspectionFieldResponse: (id) =>
      visualInspectionFieldResponseMap.get(id),
    getUserComment: (id) => userCommentMap.get(id),
    getMillPlan: (id) => millPlanMap.get(id),
    getNavyProductionDashboardConfig: (id) =>
      navyProductionDashboardConfigMap.get(id),
    getWalkdown: (id) => walkdownMap.get(id),
    getValueStory: (id) => valueStoryMap.get(id),
    getProcessFlowDiagram: (id) => processFlowDiagramMap.get(id),
    getRiskAssessmentConfig: (id) => riskAssessmentConfigMap.get(id),
    getAssetHealthPlan: (id) => assetHealthPlanMap.get(id),
    getAssetHealthAction: (id) => assetHealthActionMap.get(id),
    getComponentDeficiency: (id) => componentDeficiencyMap.get(id),
    getCase: (id) => casesMap.get(id),
    getUserList: (id) => userListMap.get(id),
    getAerospaceGVI: (id) => aerospaceGVIMap.get(id),
    getAerospaceGVICameras: (id) => aerospaceGVICamerasMap.get(id),
    getLifecycleRecommendation: (id) => lifecycleRecommendationMap.get(id),
  }
}

/**
 *
 * @param entry Rolodex Entry Json (presumably an asset model)
 * @returns the entry typed to the specific type of asset model (vertical tank, piping, etc....)
 * TODO: when we add more types of asset model expand this out to a switch
 */
function getAssetModelType(entry: RolodexEntryJson) {
  if (entry.data.type === GeckoAssetModelType.VerticalTank) {
    return entry as GeckoVerticalTankAssetModel
  } else if (entry.data.type === GeckoAssetModelType.LegacyBoiler) {
    return entry as GeckoLegacyBoilerAssetModel
  } else if (entry.data.type === GeckoAssetModelType.Piping) {
    return entry as GeckoPipelineAssetModel
  } else if (entry.data.type === GeckoAssetModelType.MachinedForging) {
    return entry as GeckoMachinedForgingAssetModel
  } else {
    return entry as GeckoAssetModel
  }
}

const MINIMUM_CDS_SCHEMA_VERSION = 6

function getComponentDatasetLayerKey(
  cdsId: string,
  columnName: string
): string {
  return `${cdsId}\u001f${columnName}`
}

/**
 * Collect the (CDS, parquet column) pairs that the loaded CDSes will dispense
 * as CDF layers from their data-frame settings. Proper CDF entries matching
 * one of these keys are skipped in favor of the CDS-derived layer.
 */
function collectComponentDatasetLayerKeys(
  rolodexProperFetchData: RolodexLoadedEntryJson[][] | undefined
): ReadonlySet<string> {
  const layerKeys = new Set<string>()
  rolodexProperFetchData?.forEach((arr) =>
    arr.forEach(({ entry }) => {
      if (entry.type !== RolodexEntryType.COMPONENT_DATASET) return

      const data = entry.data as {
        schema_version?: number
        metadata_flags?: { use_conduit?: boolean }
        data_frame_settings?: Record<string, unknown>
        ds_data_frame_settings?: Record<string, unknown>
      }
      if ((data.schema_version ?? 0) < MINIMUM_CDS_SCHEMA_VERSION) return

      const dfSettings = data.metadata_flags?.use_conduit
        ? data.data_frame_settings
        : data.ds_data_frame_settings
      Object.keys(dfSettings ?? {}).forEach((columnName) =>
        layerKeys.add(getComponentDatasetLayerKey(entry.id, columnName))
      )
    })
  )
  return layerKeys
}

/**
 * Decide whether a proper CDF entry should be dropped instead of dispensed:
 *
 * - Persisted canonical short-term layers are self-contained entries (their
 *   parquet lives on the entry itself) and are always admitted.
 * - Other CDF entries without a `_parquet_column_name` are legacy
 *   pipeline-era copies of layers that the CDSes dispense; admitting them
 *   would duplicate those layers. FDE CDFs are dropped for the same reason.
 * - A CDF whose (component-dataset tag, column) pair matches a loaded CDS's
 *   data-frame settings is superseded: those settings are the source of truth
 *   for its view configuration, and the CDS dispenses the layer itself.
 */
function shouldDropProperComponentDataFrame(
  entry: RolodexEntryJson,
  cdsProvidedLayerKeys: ReadonlySet<string>
): boolean {
  if (entry.type === RolodexEntryType.FDE_COMPONENT_DATA_FRAME) return true
  if (entry.type !== RolodexEntryType.COMPONENT_DATA_FRAME) return false
  if (isCanonicalShortTermConditionLayerEntryJson(entry)) return false

  const columnName = (
    entry.data as { _parquet_column_name?: unknown } | undefined
  )?._parquet_column_name
  if (typeof columnName !== 'string' || columnName === '') return true

  const cdsId = entry.tags?.['component-dataset']
  if (typeof cdsId !== 'string') return false

  return cdsProvidedLayerKeys.has(
    getComponentDatasetLayerKey(cdsId, columnName)
  )
}

function createComponentDataFrameEntryJsonsFromComponentDataset(
  cds: GeckoComponentDataset
): RolodexEntryJson[] {
  const dfSettingsToUse = cds.data.metadata_flags.use_conduit
    ? cds.data.data_frame_settings
    : cds.data.ds_data_frame_settings

  const filteredDfSettings = Object.entries(dfSettingsToUse ?? {}).reduce<
    Record<string, DataFrameSettings>
  >((result, [columnName, dfSettings]) => {
    const isNormallyDispensed =
      dfSettings.source.id !== GeckoSource.Nominal.id &&
      dfSettings.view.id !== GeckoView.DifferenceFromNominal.id
    if (
      !dfSettings.ignore_cdf &&
      (isNormallyDispensed || cds.hasPrecomputedEmatCategory)
    ) {
      result[columnName] = dfSettings
    }
    return result
  }, {})
  return Object.entries(filteredDfSettings ?? {}).map(([columnName]) => {
    const cdfId = `${cds.id}-${columnName}`
    const cdfTags = {
      ...cds.tags,
      ['component-dataset']: cds.id,
    }

    return {
      id: cdfId,
      type: RolodexEntryType.COMPONENT_DATA_FRAME,
      data: cds.generateCdfJson(columnName, dfSettingsToUse),
      tags: cdfTags,
      index: {},
      generation: cds.generation,
      updated_at: cds.updated_at,
      access_restriction: null,
    }
  })
}
